import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Wallet as WalletIcon, Plus, Check, Pencil, Trash2 } from 'lucide-react'
import { useWallet } from '@/hooks/useWallet'
import { WalletBasicInfo } from '@/services/wallet/storage.service'
import Snackbar, { SnackbarType } from '@/components/Snackbar'

const formatAddress = (addr: string) =>
  `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`

const Wallets = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { address, getAllWallets, switchWallet, switchWalletWithPassword, removeWallet, renameWallet } = useWallet()

  const [wallets, setWallets] = useState<WalletBasicInfo[]>([])
  const [busyAddress, setBusyAddress] = useState<string | null>(null)
  const [renamingAddress, setRenamingAddress] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [passwordPrompt, setPasswordPrompt] = useState<{ index: number; password: string } | null>(null)
  const [snackbar, setSnackbar] = useState<{ isOpen: boolean; message: string; type: SnackbarType }>({
    isOpen: false,
    message: '',
    type: 'info'
  })

  const reload = useCallback(() => {
    const { wallets: list } = getAllWallets()
    setWallets(list)
  }, [getAllWallets])

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address])

  const handleSelect = async (index: number, walletAddress: string) => {
    if (walletAddress.toLowerCase() === address?.toLowerCase()) return
    setBusyAddress(walletAddress)
    const result = await switchWallet(index)
    setBusyAddress(null)
    if (result === 'wrong_password') {
      setPasswordPrompt({ index, password: '' })
    }
  }

  const handlePasswordConfirm = async () => {
    if (!passwordPrompt) return
    setBusyAddress(wallets[passwordPrompt.index]?.address ?? null)
    const success = await switchWalletWithPassword(passwordPrompt.index, passwordPrompt.password)
    setBusyAddress(null)
    if (success) {
      setPasswordPrompt(null)
    } else {
      setSnackbar({ isOpen: true, message: t('dialogs.wrong_password'), type: 'error' })
    }
  }

  const handleStartRename = (walletAddress: string, currentName?: string) => {
    setRenamingAddress(walletAddress)
    setRenameValue(currentName || '')
  }

  const handleConfirmRename = (walletAddress: string) => {
    if (renameValue.trim()) {
      renameWallet(walletAddress, renameValue.trim())
      reload()
    }
    setRenamingAddress(null)
  }

  const handleRemove = async (walletAddress: string) => {
    if (wallets.length <= 1) return
    if (!window.confirm(t('wallets.confirm_remove'))) return

    setBusyAddress(walletAddress)
    const result = await removeWallet(walletAddress)
    setBusyAddress(null)

    if (result === 'ok') {
      reload()
      setSnackbar({ isOpen: true, message: t('wallets.removed'), type: 'success' })
    } else if (result === 'last_wallet') {
      setSnackbar({ isOpen: true, message: t('wallets.cannot_remove_last'), type: 'error' })
    } else {
      setSnackbar({ isOpen: true, message: t('wallets.remove_error'), type: 'error' })
    }
  }

  return (
    <div className="flex flex-col w-full max-w-xl mx-auto gap-6 pt-4">
      <div className="hidden lg:flex items-center gap-4">
        <h1 className="text-3xl font-bold text-text-main">{t('wallets.title')}</h1>
      </div>

      <div className="bg-card border border-border-base rounded-xl shadow-sm divide-y divide-border-base overflow-hidden">
        {wallets.map((wallet, index) => {
          const isActive = wallet.address.toLowerCase() === address?.toLowerCase()
          const isBusy = busyAddress?.toLowerCase() === wallet.address.toLowerCase()
          const isRenaming = renamingAddress === wallet.address

          return (
            <div key={wallet.address} className="flex items-center gap-3 px-4 py-3.5">
              <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <WalletIcon className="w-4 h-4 text-primary" />
              </div>

              <button
                onClick={() => handleSelect(index, wallet.address)}
                disabled={isBusy}
                className="flex-1 flex flex-col items-start text-left min-w-0 disabled:opacity-60"
              >
                {isRenaming ? (
                  <input
                    autoFocus
                    value={renameValue}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => handleConfirmRename(wallet.address)}
                    onKeyDown={(e) => e.key === 'Enter' && handleConfirmRename(wallet.address)}
                    className="bg-transparent border-b border-primary text-sm font-medium text-text-main outline-none w-full"
                  />
                ) : (
                  <span className="text-sm font-medium text-text-main truncate w-full">
                    {wallet.name || t('wallets.default_name')}
                  </span>
                )}
                <span className="text-xs text-text-secondary font-mono">
                  {formatAddress(wallet.address)}
                </span>
              </button>

              {isActive && <Check className="w-5 h-5 text-success shrink-0" />}

              <button
                onClick={() => handleStartRename(wallet.address, wallet.name)}
                className="p-1.5 rounded-full text-text-secondary hover:text-text-main transition-colors shrink-0"
                aria-label={t('wallets.rename')}
              >
                <Pencil className="w-4 h-4" />
              </button>

              <button
                onClick={() => handleRemove(wallet.address)}
                disabled={wallets.length <= 1 || isBusy}
                className="p-1.5 rounded-full text-text-secondary hover:text-red-500 transition-colors shrink-0 disabled:opacity-30 disabled:hover:text-text-secondary"
                aria-label={t('wallets.remove')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )
        })}
      </div>

      <button
        onClick={() => navigate('/add-wallet')}
        className="btn-gradient-fill flex font-bold items-center justify-center gap-2 rounded-full py-4 px-6"
      >
        <Plus className="w-5 h-5" />
        {t('wallets.add_title')}
      </button>

      {passwordPrompt && (
        <div className="fixed inset-0 bg-opacity-50 backdrop-blur-xs flex items-center justify-center z-50 px-4">
          <div className="bg-card border border-border-base rounded-2xl p-6 w-full max-w-sm flex flex-col gap-4">
            <p className="text-sm font-medium text-text-main">{t('wallets.enter_password_for_wallet')}</p>
            <input
              type="password"
              autoFocus
              value={passwordPrompt.password}
              onChange={(e) => setPasswordPrompt({ ...passwordPrompt, password: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handlePasswordConfirm()}
              className="w-full p-3 rounded-xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none text-text-main"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setPasswordPrompt(null)}
                className="flex-1 py-2.5 rounded-full border border-border-base text-text-main font-medium"
              >
                {t('dialogs.cancel')}
              </button>
              <button
                onClick={handlePasswordConfirm}
                className="flex-1 btn-gradient-fill rounded-full py-2.5 font-bold"
              >
                {t('dialogs.verify_continue')}
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        message={snackbar.message}
        isOpen={snackbar.isOpen}
        type={snackbar.type}
        onClose={() => setSnackbar((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default Wallets
